import { TimelineEventStatus } from '../models/enums/timeline-event-status.enum';
import { TimelineEvent } from '../models/timeline-event.model';
import { getBusinessDayNumberPt } from './timeline-dates';

// [#1307 follow-up, Roman 21-Oct] An engagement's Gantt reads as of now: what is done on top, then what is open, then
// what is to come - each in the order it started or will start. Rows used to follow the stage order, so a step of a
// late stage that ran early (an LOI step with nothing to wait for) sat below steps still to come.
export enum GanttRowProgress {
    Done = 0,
    Open = 1,
    ToCome = 2,
}

export const progressOf = (status: TimelineEventStatus): GanttRowProgress => {
    switch (status) {
        case TimelineEventStatus.Completed:
        case TimelineEventStatus.Cancelled:
            return GanttRowProgress.Done;
        case TimelineEventStatus.InProgress:
        case TimelineEventStatus.Late:
            return GanttRowProgress.Open;
        default:
            return GanttRowProgress.ToCome;
    }
};

export interface GanttRowInProgress {
    progress: GanttRowProgress;
    startUtc: Date;
}

// Rows that tie keep the order they came in: the stage order, then the order inside the stage.
export const inOrderOfProgress = <T extends GanttRowInProgress>(rows: T[]): T[] =>
    rows
        .map((row, index) => ({ row, index }))
        .sort(
            (a, b) =>
                a.row.progress - b.row.progress ||
                a.row.startUtc.getTime() - b.row.startUtc.getTime() ||
                a.index - b.index
        )
        .map(({ row }) => row);

// [#1307 follow-up, Roman 21-Oct] An engagement's Gantt starts on the day its timeline's first step does - for a Buyer
// who joined during a catch-up, the catch-up's first day, not the engagement's - and never before the engagement's
// own first day. Day 0 of "Days Since Start" is that day.
export const firstDayOfTheRowsPt = (rows: { type?: string; start?: Date }[], engagementFirstDayPt: number): number => {
    const rowDays = rows
        .filter((row) => row.type === 'line' && !!row.start)
        .map((row) => getBusinessDayNumberPt(row.start));
    return rowDays.length > 0 ? Math.max(engagementFirstDayPt, Math.min(...rowDays)) : engagementFirstDayPt;
};

// [#1307 follow-up, Roman 21-Oct] A step that has not started cannot begin before now. The API plans it from today at
// the earliest (whole days); its bar begins now at the earliest, not at the start of today under the now line.
export const startNotBeforeNow = (event: TimelineEvent, plannedStartUtc: Date, nowUtc: Date): Date => {
    const isNotStarted = !event.startedAt && event.status === TimelineEventStatus.Upcoming;
    return isNotStarted && plannedStartUtc < nowUtc ? nowUtc : plannedStartUtc;
};
